// RV-1 — Launch Reviews content helpers (pure, no React). How an item's text becomes what the
// room shows: which frame (social post / email / text), which versions, and tracked changes.

export type Version = { subject?: string; preview?: string; sender?: string; body?: string; note?: string };
export type Item = { id: string; n?: number; title?: string; status?: string; versions?: Record<string, Version>; section?: string; here?: Here; options?: Opt[] };
// deploy 9 — sections, "you are here" and options. All optional: a room without them looks as before.
// A section groups cards (its intro card comes before its first item); map is an SVG string.
export type RoomSection = { id: string; title: string; sub?: string; intro?: string; map?: string };
// where this item sits: a map already drawn with it highlighted, a "Project › Section › Item" path, short facts
export type Here = { map?: string; path?: string; lines?: { label: string; text: string }[] };
// a few choices on a card (e.g. three subject lines); pick is the recommended one's position
export type Opt = { label: string; choices: { text: string; note?: string }[]; pick?: number };
export type Meta = { order?: string[]; labels?: Record<string, string>; compare?: [string, string] };
export type Decision = { decision: "approve" | "change" | "reject" | "question" | "answer"; reasons?: string[]; note?: string; version?: string; answer?: string; choices?: (number | null)[] };
export type Sections = { graphic?: string; reel?: string; fb?: string; ig?: string; li?: string };

const HEADS: Record<string, keyof Sections> = { "On the graphic": "graphic", "Reel, on screen": "reel", Facebook: "fb", Instagram: "ig", LinkedIn: "li" };

// "On the graphic / Facebook / Instagram / LinkedIn" blocks → their text; null when it isn't a social post
export function sectionsOf(body = ""): Sections | null {
  const out: Sections = {};
  let cur: keyof Sections | null = null;
  let buf: string[] = [];
  const flush = () => { if (cur) out[cur] = buf.join("\n").trim(); };
  for (const line of String(body).split("\n")) {
    const head = HEADS[line.trim()];
    if (head) { flush(); cur = head; buf = []; } else buf.push(line);
  }
  flush();
  return out.fb || out.ig || out.li ? out : null;
}

export function frameOf(v: Version = {}): "social" | "email" | "text" {
  if (sectionsOf(v.body)) return "social";
  if (v.subject) return "email";
  return "text";
}

export function versionsOf(item: Item, meta: Meta = {}) {
  const keys = Object.keys(item.versions || {});
  const order = (meta.order || []).filter((k) => keys.includes(k));
  return [...order, ...keys.filter((k) => !order.includes(k))].map((key) => ({ key, label: (meta.labels && meta.labels[key]) || key }));
}

// the two versions "Changes" compares: the campaign's pair, else first and last
export function comparePair(item: Item, meta: Meta = {}): [string, string] | null {
  const keys = Object.keys(item.versions || {});
  if (meta.compare && keys.includes(meta.compare[0]) && keys.includes(meta.compare[1])) return meta.compare;
  return keys.length > 1 ? [keys[0], keys[keys.length - 1]] : null;
}

export type Seg = { t: "same" | "ins" | "del"; s: string };

// word-level diff (longest common subsequence over words + spaces), merged into runs
export function wordDiff(a = "", b = ""): Seg[] {
  const A = a.split(/(\s+)/).filter((x) => x !== ""), B = b.split(/(\s+)/).filter((x) => x !== "");
  const n = A.length, m = B.length;
  if (n * m > 600000) return [{ t: "same", s: b }];
  const L = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) L[i][j] = A[i] === B[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const out: Seg[] = [];
  const push = (t: Seg["t"], s: string) => { const last = out[out.length - 1]; if (last && last.t === t) last.s += s; else out.push({ t, s }); };
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { push("same", A[i]); i++; j++; }
    else if (L[i + 1][j] >= L[i][j + 1]) { push("del", A[i]); i++; }
    else { push("ins", B[j]); j++; }
  }
  while (i < n) push("del", A[i++]);
  while (j < m) push("ins", B[j++]);
  return phrases(out.filter((sg) => sg.s !== ""));
}

// A rewrite read word by word zig-zags ("You~~Picture~~ don't~~4:15~~ …"). Join each run of changes,
// and any short kept words caught between them, into one struck phrase followed by one new phrase.
const short = (s: string) => s.trim().split(/\s+/).filter(Boolean).length <= 2;
function phrases(segs: Seg[]): Seg[] {
  const out: Seg[] = [];
  let i = 0;
  while (i < segs.length) {
    if (segs[i].t === "same") { out.push(segs[i++]); continue; }
    let del = "", ins = "";
    for (;;) {
      while (i < segs.length && segs[i].t !== "same") { if (segs[i].t === "del") del += segs[i].s; else ins += segs[i].s; i++; }
      if (i + 1 < segs.length && short(segs[i].s)) { del += segs[i].s; ins += segs[i].s; i++; continue; }
      break;
    }
    if (del) out.push({ t: "del", s: del });
    if (ins) out.push({ t: "ins", s: ins });
  }
  return out;
}

// NR-1 — every Can review reviewer's call on one item, for the author's side-by-side view (name order;
// someone who hasn't decided it yet shows as "Not yet")
const CALL_LABEL: Record<string, string> = { approve: "Approved", change: "Changes", reject: "Rejected", question: "Question", answer: "Answered" };
export type ReviewerCall = { email: string; name: string; label: string; decision: Decision["decision"] | null; note: string | undefined };
export function reviewerCalls(reviewers: Record<string, { decisions?: Record<string, Decision> }> | undefined, people: Record<string, { name: string }> | undefined, itemId: string): ReviewerCall[] {
  return Object.entries(reviewers || {})
    .map(([email, r]) => {
      const d = r.decisions?.[itemId];
      return { email, name: people?.[email]?.name || email, label: d ? CALL_LABEL[d.decision] || d.decision : "Not yet", decision: d ? d.decision : null, note: d ? d.answer || d.note || (d.reasons?.length ? d.reasons.join(", ") : undefined) : undefined };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function itemStatus(item: Item, d?: Decision): "approved" | "changes" | "ready" | "look" {
  if (d) return d.decision === "approve" ? "approved" : "changes";
  return item.status === "good" ? "ready" : "look";
}

const label = (it: Item, i: number) => `${it.n ?? i + 1}`;

// the reply drafted for the original thread (posted only after the reviewer says yes)
export function draftReply(review: { title: string; source?: { sender?: string }; items: Item[]; decisions: Record<string, Decision> }): string {
  const who = review.source?.sender ? `Thanks, ${review.source.sender}.` : "Thanks.";
  const ok: string[] = [], change: string[] = [], open: string[] = [];
  review.items.forEach((it, i) => {
    const d = review.decisions[it.id];
    if (!d) open.push(label(it, i));
    else if (d.decision === "approve") ok.push(label(it, i));
    else change.push(`• ${label(it, i)}. ${it.title || it.id}: ${[...(d.reasons || []), d.note].filter(Boolean).join("; ")}`);
  });
  const parts = [`${who} ${review.title} is reviewed.`, `Approved (${ok.length}): ${ok.join(", ") || "none"}.`];
  if (change.length) parts.push(`Needs changes (${change.length}):\n${change.join("\n")}`);
  const picks = review.items.flatMap((it, i) => choiceLines(it, review.decisions[it.id]).map((l) => `• ${label(it, i)}. ${it.title || it.id} — ${l}`));
  if (picks.length) parts.push(`Choices:\n${picks.join("\n")}`);
  if (open.length) parts.push(`Still open (${open.length}): ${open.join(", ")}.`);
  return parts.join("\n\n");
}

// RV-6 — Page room. A page body written as labeled parts ("Shown to:", "Result line:", "On screen:",
// "Email 1 …:") splits so the room can show the page in a browser frame and its email in an inbox;
// anything without those labels is shown whole as the page.
export type PageParts = { audience: string; headline: string; screen: string; emailLabel: string; email: string };
export function pageParts(body = ""): PageParts {
  const out: PageParts = { audience: "", headline: "", screen: "", emailLabel: "", email: "" };
  const lines = body.split("\n");
  let into: "screen" | "email" | null = null;
  const screen: string[] = [], email: string[] = [], loose: string[] = [];
  let labeled = false;
  for (const line of lines) {
    let m;
    if ((m = /^Shown to:\s*(.*)$/i.exec(line))) { out.audience = m[1].trim(); labeled = true; into = null; continue; }
    if ((m = /^Result line:\s*(.*)$/i.exec(line))) { out.headline = m[1].trim(); labeled = true; into = null; continue; }
    if (/^On screen:\s*$/i.test(line)) { into = "screen"; labeled = true; continue; }
    if ((m = /^(Email \d+[^:]*):\s*$/i.exec(line))) { out.emailLabel = m[1].trim(); into = "email"; labeled = true; continue; }
    if (into === "screen") screen.push(line); else if (into === "email") email.push(line); else loose.push(line);
  }
  if (!labeled) return { ...out, screen: body.trim() };
  out.screen = (screen.length ? screen : loose).join("\n").trim();
  out.email = email.join("\n").trim();
  return out;
}

// "The option is the whole set": mixing versions across items is allowed, with a warning.
// Returns the groups when the approved items come from more than one version, else nothing.
export function versionMix(items: Item[], decisions: Record<string, Decision>): { version: string; items: number[] }[] {
  const groups = new Map<string, number[]>();
  items.forEach((it, i) => {
    const d = decisions[it.id];
    if (d && d.decision === "approve" && d.version) groups.set(d.version, [...(groups.get(d.version) || []), it.n ?? i + 1]);
  });
  return groups.size > 1 ? [...groups].map(([version, n]) => ({ version, items: n })) : [];
}

// RV-6 — what a Questions or Decision review sends back: one line per call, in words Claude can
// read back and apply to the plan or spec the questions came from.
export function answerList(review: { title: string; items: (Item & { rec?: string })[]; decisions: Record<string, Decision> }): string {
  const lines: string[] = [], open: string[] = [];
  review.items.forEach((it, i) => {
    const d = review.decisions[it.id], n = label(it, i), t = it.title || it.id;
    if (!d) return void open.push(n);
    const why = [...(d.reasons || []).filter((r) => r !== "Disapproved"), d.note].filter(Boolean).join("; ");
    if (d.decision === "approve") lines.push(`${n}. ${t}: ${it.rec ? `take the recommendation (${it.rec}).` : "approved."}`);
    else if (d.decision === "answer") lines.push(`${n}. ${t}: ${d.answer}`);
    else if (d.decision === "question") lines.push(`${n}. ${t}: question first. ${why}`);
    else lines.push(`${n}. ${t}: no. ${why}`);
    for (const c of choiceLines(it, d)) lines.push(`   ${c}`);
  });
  const out = [`${review.title}: decisions (${lines.length} of ${review.items.length}).`, ...lines];
  if (open.length) out.push(`Still open: ${open.join(", ")}.`);
  return out.join("\n");
}

// RV-6 — a light reading of the markdown the skills write (SIRs, question details): paragraphs,
// "- " lists and | pipe | tables.
export type MdBlock = { kind: "p" | "ul"; lines: string[] } | { kind: "table"; head: string[]; rows: string[][] };
export function mdBlocks(text = ""): MdBlock[] {
  const out: MdBlock[] = [];
  const cells = (l: string) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
  const lines = String(text).split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trimEnd();
    const last = out[out.length - 1];
    if (/^\s*\|.*\|\s*$/.test(line)) {
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { if (!/^\s*\|[\s:|-]+\|\s*$/.test(lines[i])) rows.push(cells(lines[i])); i++; }
      i--;
      out.push({ kind: "table", head: rows[0] || [], rows: rows.slice(1) });
      continue;
    }
    if (!line.trim()) { out.push({ kind: "p", lines: [] }); continue; }
    const li = /^\s*[-•*]\s+(.*)$/.exec(line);
    if (li) { if (last && last.kind === "ul") last.lines.push(li[1]); else out.push({ kind: "ul", lines: [li[1]] }); }
    else if (last && last.kind === "p" && last.lines.length) last.lines.push(line);
    else out.push({ kind: "p", lines: [line] });
  }
  return out.filter((b) => b.kind === "table" || b.lines.length);
}

// RV-6 — read-along. The audio's word timings ([start, end] per token, null for spaces) say which
// word to light up: the last word that has started by now.
export function wordAt(times: ([number, number] | null)[], t: number): number {
  let at = -1;
  for (let i = 0; i < times.length; i++) { const w = times[i]; if (!w) continue; if (w[0] <= t) at = i; else break; }
  return at;
}

// RV-6 — Page room "Sections" view (the copy room's alternatives): each section of a page
// ("Headline: …" lines in each version's text), with every version's option for it, today's first.
export function sectionOptions(item: Item, order: string[] = []): { label: string; options: { key: string; text: string }[] }[] {
  const keys = [...order.filter((k) => item.versions?.[k]), ...Object.keys(item.versions || {}).filter((k) => !order.includes(k))];
  const rows = new Map<string, { key: string; text: string }[]>();
  for (const k of keys) {
    for (const line of (item.versions?.[k]?.body || "").split("\n")) {
      const m = /^([^:]{1,40}):\s?(.*)$/.exec(line);
      if (!m || !m[2].trim()) continue;
      if (!rows.has(m[1])) rows.set(m[1], []);
      rows.get(m[1])!.push({ key: k, text: m[2].trim() });
    }
  }
  return [...rows].map(([label, options]) => ({ label, options }));
}

// what gets recorded as approved: the version letter when every pick agrees, else the mix
export function pickedVersion(picks: Record<string, string>): string {
  const vals = Object.values(picks);
  if (!vals.length) return "";
  return new Set(vals).size === 1 ? vals[0] : `mix: ${Object.entries(picks).map(([k, v]) => `${k} ${v}`).join(", ")}`;
}

// Nested reviews: a document room linked from an open review's item is that review's child. The
// server marks it (partOf) and gives the parent its documents (docs) plus a roll-up of the notes and
// decisions on them. The lists show only the top level; the parent's row carries a small tag.
export type Nested = { partOf?: { id: string; title: string } | null; docs?: string[]; docNotes?: number; docDecided?: number };
export const topLevel = <T extends Nested>(reviews: T[]): T[] => reviews.filter((r) => !r.partOf);
export function docTag(r: Nested): string | null {
  const n = (r.docs || []).length;
  if (!n) return null;
  const doc = n === 1 ? "the document" : "the documents";
  const parts = [`📄 ${n} document${n === 1 ? "" : "s"}`];
  if (r.docNotes) parts.push(`${r.docNotes} note${r.docNotes === 1 ? "" : "s"} on ${doc}`);
  else if (r.docDecided) parts.push(`${r.docDecided} decided on ${doc}`);
  return parts.join(" · ");
}

// ---- deploy 9: sections, "you are here", options ----

// a map is shown as an image built from its SVG text, never put into the page as markup, so a map
// can't run script. Anything that isn't SVG gives nothing.
export function svgSrc(svg?: unknown): string | null {
  if (typeof svg !== "string") return null;
  const t = svg.trim();
  if (!/^(<\?xml[^>]*>\s*)?(<!--[\s\S]*?-->\s*)*<svg[\s>/]/i.test(t)) return null;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(t)}`;
}

// plain intro text → paragraphs (split on blank lines)
export const paragraphs = (text?: string): string[] => String(text || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

// what you move through: each section's intro card, then its items. An intro comes once, before
// the first item of its section; items with no (known) section just follow.
export type FlowEntry<T extends Item = Item> = { kind: "intro"; section: RoomSection } | { kind: "item"; item: T; i: number };
export function flowOf<T extends Item>(items: T[], sections?: RoomSection[] | null): FlowEntry<T>[] {
  const byId = new Map((sections || []).map((s) => [s.id, s]));
  const seen = new Set<string>();
  const out: FlowEntry<T>[] = [];
  items.forEach((item, i) => {
    const s = item.section ? byId.get(item.section) : undefined;
    if (s && !seen.has(s.id)) { seen.add(s.id); out.push({ kind: "intro", section: s }); }
    out.push({ kind: "item", item, i });
  });
  return out;
}

// the item list grouped under section headers, in the order sections first appear (sections with
// no items are left out); items with no known section go in a last, untitled group
export function sectionGroups<T extends Item>(items: T[], sections?: RoomSection[] | null): { section: RoomSection | null; items: { item: T; i: number }[] }[] {
  const byId = new Map((sections || []).map((s) => [s.id, s]));
  const groups = new Map<string, { section: RoomSection | null; items: { item: T; i: number }[] }>();
  items.forEach((item, i) => {
    const s = (item.section && byId.get(item.section)) || null;
    const key = s ? s.id : "";
    if (!groups.has(key)) groups.set(key, { section: s, items: [] });
    groups.get(key)!.items.push({ item, i });
  });
  const loose = groups.get("");
  groups.delete("");
  return [...groups.values(), ...(loose ? [loose] : [])];
}

const validPick = (o: Opt, k: unknown): k is number => Number.isInteger(k) && (k as number) >= 0 && (k as number) < (o.choices || []).length;

// which choice a card shows as chosen: the one just tapped, else the saved one, else the recommended
export function choiceOf(o: Opt, k: number, saved?: (number | null)[], local?: (number | null)[]): number | null {
  for (const c of [local?.[k], saved?.[k], o.pick]) if (validPick(o, c)) return c;
  return null;
}

// how a decided item's choices read back: "Subject line: chose “X” (recommended “Y”)." or "kept the recommended"
export function choiceLines(item: Item, d?: Decision): string[] {
  if (!d || !item.options?.length) return [];
  return item.options.map((o, k) => {
    const rec = validPick(o, o.pick) ? o.pick! : null;
    const c = d.choices?.[k];
    const chose = validPick(o, c) ? c : null;
    const text = (j: number) => `“${o.choices[j].text}”`;
    if (chose !== null && chose !== rec) return `${o.label}: chose ${text(chose)}${rec !== null ? ` (recommended ${text(rec)})` : ""}.`;
    if (rec !== null) return `${o.label}: kept the recommended (${text(rec)}).`;
    return `${o.label}: no choice made.`;
  });
}
